// This file is auto-generated. Do not edit manually.

import { z } from "zod";
import { CreatePropertySchema, UpdatePropertySchema } from "./propertySchemas";
import {
  CreateRequestDetailSchema,
  UpdateRequestDetailSchema,
} from "./requestDetailSchema";
import {
  CreateStakeholderSchema,
  UpdateStakeholderSchema,
} from "./requestStakeholderSchemas";
import {
  coerceBoolean,
  ResponseMessageSchema,
  StatusSchema,
  UuidSchema,
} from "./utilitySchemas";
import { FileResponseSchema, FileSystemTags } from "./fileSchemas";
import {
  AppliedDiscountCodeSchema,
  InvoiceSchema,
  PaymentStatusEnum,
} from "./invoiceSchemas";
import { SiteSourceEnum } from "./partnerSiteSchemas";
import {
  RequestPriceListSchema,
  StripePriceListSchema,
} from "./pricelistSchemas";
import { EntityPrioritySchema } from "./entitySchemas";

export const RequestStatus = z.enum([
  "received",
  "inProgress",
  "initialAssessment",
  "sent",
  "completed",
  "revision",
  "cancelled",
  "paused",
  "archived",
  "incomplete",
  "draft",
  "deleted",
  "ordered",
  "inCreation",
]);
export type RequestStatusType = z.infer<typeof RequestStatus>;

const RequestVariantEnum = z.enum([
  "standard",
  "liteRNDG",
  "fullLiteRNDG",
  "unqualifiedLiteRNDG",
]);
export type RequestVariantEnumType = z.infer<typeof RequestVariantEnum>;

export const ViewingAddonIdentifiersEnum = z.enum([
  "digitalInspection",
  "onSiteInspection",
  "exteriorOnSiteInspection",
  "interiorExteriorOnSiteInspection",
  "none",
  "undecided",
]);

export type ViewingAddonIdentifiersEnumType = z.infer<
  typeof ViewingAddonIdentifiersEnum
>;

export const RequestViewingStatusSchema = z.enum([
  "COMPLETE",
  "SCHEDULED",
  "TO_BE_SCHEDULED",
  "NONE",
]);
export type RequestViewingStatusType = z.infer<
  typeof RequestViewingStatusSchema
>;

export const RequestDocumentStatusSchema = z.enum([
  "PENDING",
  "NOT_REQUIRED",
  "SKIPPED",
  "WAITING",
  "IN_PROGRESS",
  "COMPLETE",
]);
export type RequestDocumentStatusType = z.infer<
  typeof RequestDocumentStatusSchema
>;

const GuestDocumentUploadDecisionStatusSchema = z.enum([
  "COMPLETE",
  "IN_PROGRESS",
  "NOT_REQUIRED",
  "SKIPPED",
]);

const RequestViewingPayloadSchema = z.object({
  status: RequestViewingStatusSchema,
  scheduledFor: z
    .string()
    .refine((value) => !Number.isNaN(Date.parse(value)), {
      message: "Invalid scheduledFor date",
    })
    .nullable()
    .optional(),
});

const RequestViewingStatusDetailSchema = z.object({
  id: z.string(),
  status: RequestViewingStatusSchema,
  statusChangedAt: z.string(),
  scheduledFor: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type RequestViewingDetailType = z.infer<
  typeof RequestViewingStatusDetailSchema
>;

export const LegalFeeAddonIdentifiersEnum = z.enum([
  "taxLawPlausibilityReview",
  "none",
]);

export type LegalFeeAddonIdentifiersEnumType = z.infer<
  typeof LegalFeeAddonIdentifiersEnum
>;

export const NinoxSyncRecommendationEnum = z.enum([
  "syncToNinox",
  "syncFromNinox",
  "review",
  "none",
]);
export type NinoxSyncRecommendationEnumType = z.infer<
  typeof NinoxSyncRecommendationEnum
>;

export const CreateRequestSchema = z.object({
  type: z.string(),
  status: RequestStatus.default("inProgress"),
  source: SiteSourceEnum.optional(),
  requestIsLiteRNDG: z.boolean().optional(),
  attributionKey: z.string().optional(),
  // API will need this only when the request is internal and user is super admin
  appraiserId: z.string().optional(),
  recaptchaToken: z.string().optional(),
  honeypot: z.string().optional(),
});
export type CreateRequest = z.infer<typeof CreateRequestSchema>;

export const createPaymentLinkSchema = z.object({
  requestId: z.string(),
  isExternalRequest: z.boolean(),
  customPrice: z.number().optional(),
});

export const createPaymentLinkPostmanSchema = z.object({
  evalionId: z.string(),
  isExternalRequest: z.boolean(),
  customPrice: z.number().optional(),
  excludePrePayDiscount: z.boolean(),
  postman: z.boolean(),
});

export type CreatePaymentLink = z.infer<typeof createPaymentLinkSchema>;

export const CreatePaymentLinkResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.string(),
});

export const RequestSortByEnum = z.enum([
  "id",
  "type",
  "status",
  "createdAt",
  "updatedAt",
  "property",
  "user",
  "invoice",
  "assignee",
  "assignedAt",
  "internalId",
  "submittedAt",
  "orderedAt",
  "source",
  "appraiser",
  "requestVariant",
  "aptDescription",
  "accountOwner",
  "viewingStatus",
  "deadlineDate",
]);

export type RequestSortByEnumType = z.infer<typeof RequestSortByEnum>;

export const RequestQuerySchema = z.object({
  limit: z.coerce.number().int().positive().optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
  sortBy: RequestSortByEnum.optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
  filter: z
    .object({
      type: z.string().optional(),
      status: z.array(RequestStatus).optional(),
      typeOfRequest: z.array(z.string()).optional(),
      paymentStatus: z.array(PaymentStatusEnum).optional(),
      entityPriority: z.array(EntityPrioritySchema).optional(),
      requestVariant: z.array(RequestVariantEnum).optional(),
      viewing: z.array(ViewingAddonIdentifiersEnum).optional(),
      viewingStatus: z.array(RequestViewingStatusSchema).optional(),
      documentStatus: z.array(RequestDocumentStatusSchema).optional(),
      assigneeId: z
        .union([
          z.coerce.number(),
          z.literal("null"), // Explicitly allow null
        ])
        .optional(),
      accountOwner: z
        .array(
          z.union([
            z.coerce.number(),
            z.literal("null"),
            z.literal("no-owner"),
          ]),
        )
        .optional(),
      userId: z.number().optional(),
      propertyId: z.string().optional(),
      createdAtFrom: z.string().datetime().optional(),
      createdAtTo: z.string().datetime().optional(),
      updatedAtFrom: z.string().datetime().optional(),
      updatedAtTo: z.string().datetime().optional(),
      assignedAtFrom: z.string().datetime().optional(),
      assignedAtTo: z.string().datetime().optional(),
      hasInvoice: coerceBoolean().optional(),
      hasProperty: coerceBoolean().optional(),
      isFlagged: coerceBoolean().optional(),
      viewOpen: coerceBoolean().optional(),
      hideCancelledArchived: coerceBoolean().optional(),
      search: z.string().optional(),
      source: z.array(SiteSourceEnum).optional(),
      appraiser: z.string().optional(),
    })
    .optional(),
  isExternal: z
    .union([
      z.boolean(),
      z.enum(["true", "false"]).transform((val) => val === "true"),
    ])
    .optional(),
  viewAll: coerceBoolean().optional(),
});

export type RequestQuery = z.infer<typeof RequestQuerySchema>;

export const RequestFilterQuerySchema = RequestQuerySchema.shape.filter;
export type RequestFilterQuery = z.infer<typeof RequestFilterQuerySchema>;

export const RequestAllocationSchema = z.object({
  property: CreatePropertySchema.optional(),
  stakeholders: z.array(CreateStakeholderSchema).optional(),
});
export type RequestAllocation = z.infer<typeof RequestAllocationSchema>;

export const RequestAllocationUpdateSchema = z.object({
  property: UpdatePropertySchema.optional(),
  stakeholders: z.array(UpdateStakeholderSchema).optional(),
});
export type RequestAllocationUpdate = z.infer<
  typeof RequestAllocationUpdateSchema
>;

export const RequestDetailsSchema = z.object({
  details: z.array(CreateRequestDetailSchema),
});
export type RequestDetails = z.infer<typeof RequestDetailsSchema>;

export const RequestDetailsUpdateSchema = z.object({
  details: z.array(UpdateRequestDetailSchema),
});
export type RequestDetailsUpdate = z.infer<typeof RequestDetailsUpdateSchema>;

export const UpdateRequestStatusSchema = CreateRequestSchema.omit({
  type: true,
});
export type UpdateRequestStatus = z.infer<typeof UpdateRequestStatusSchema>;

const PropertySchema = z.object({
  id: z.string(),
  streetName: z.string(),
  streetNumber: z.string(),
  postalCode: z.string(),
  city: z.string(),
  country: z.string(),
  description: z.string().nullable(),
  moreInfo: z.string().nullable(),
  type: z.string(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  formattedAddress: z.string().nullable().optional(),
  geocodingProvider: z.string().nullable().optional(),
  geocodingProviderId: z.string().nullable().optional(),
  geocodingMetadata: z.unknown().nullable().optional(),
  coordinatesVerifiedAt: z.date().or(z.string()).nullable().optional(),
});

const UserSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  active: z.boolean(),
});

export const OverviewPropertySchema = z.object({
  id: z.string().optional(),
  streetName: z.string(),
  streetNumber: z.string(),
  postalCode: z.string(),
  city: z.string(),
  country: z.string(),
  description: z.string().nullable(),
  moreInfo: z.string().nullable(),
  type: z.string(),
  entryType: z.string(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  formattedAddress: z.string().nullable().optional(),
  geocodingProvider: z.string().nullable().optional(),
  geocodingProviderId: z.string().nullable().optional(),
  geocodingMetadata: z.unknown().nullable().optional(),
  coordinatesVerifiedAt: z.date().or(z.string()).nullable().optional(),
});
export type OverviewProperty = z.infer<typeof OverviewPropertySchema>;
export const OverviewRequestDetailSchema = z.object({
  id: z.number(),
  fieldGroup: z.string(),
  fieldType: z.string(),
  fieldName: z.string(),
  fieldValue: z.string(),
});
export type OverviewRequestDetail = z.infer<typeof OverviewRequestDetailSchema>;

export const RequestSchema = z.object({
  id: z.string(),
  type: z.string(),
  status: z.string(),
  documentStatus: RequestDocumentStatusSchema,
  source: SiteSourceEnum,
  property: PropertySchema.nullable(),
  user: UserSchema,
  isFlagged: z.boolean(),
  entityPriority: EntityPrioritySchema.default("NOT_SET"),
  assignee: UserSchema.nullable(),
  assignedAt: z.string().nullable(),
  assignedBy: z.number().nullable(),
  assignedByUser: UserSchema.nullable(),
  accountOwnerId: z.number().nullable(),
  noAccountOwner: z.boolean(),
  accountOwner: UserSchema.nullable(),
  invoice: InvoiceSchema.optional().nullable(),
  requestDetails: z.array(OverviewRequestDetailSchema),
  initialAssessmentSent: z.object({
    isSent: z.boolean(),
    sentAt: z.string().datetime().nullable(),
    sentByName: z.string().nullable(),
    sendAction: z.enum(["send", "resend"]).nullable(),
  }),
  appraiser: z.object({
    id: z.string(),
    identifier: z.string(),
    name: z.string(),
  }),
  createdAt: z.string(),
  updatedAt: z.string(),
  deadlineDate: z.string().nullable().optional(),
  evalionId: z.string(),
  submittedAt: z.string(),
  orderedAt: z.string().nullable(),
  lastNinoxSync: z.string().datetime().nullable(),
  requiresNinoxSync: z.boolean(),
  canMigrate: z.boolean(),
  wasExternal: z.boolean().optional(),
  isExternal: z.boolean(),
  requestIsLiteRNDG: z.boolean().nullable(),
  requestWasLiteRNDG: z.boolean().nullable(),
  liteSubmittedAt: z.string().nullable(),
  requestVariant: RequestVariantEnum,
  isQualifiedLiteRequest: z.boolean().nullable(),
  isQualifiedLiteRequestUpdatedAt: z.string().nullable(),
  viewingTypes: z.array(ViewingAddonIdentifiersEnum),
  viewing: RequestViewingStatusDetailSchema.nullable().optional(),
});
// Test

export type Request = z.infer<typeof RequestSchema>;

const PaginationInfoSchema = z.object({
  total: z.number(),
  limit: z.number(),
  offset: z.number(),
  hasMore: z.boolean(),
  totalUnassigned: z.number().nullable(),
  totalWithoutAccountOwner: z.number().nullable(),
});

export type RequestPaginationInfo = z.infer<typeof PaginationInfoSchema>;

export const RequestResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    requests: z.array(RequestSchema),
    paginationInfo: PaginationInfoSchema,
  }),
});

export type RequestResponse = z.infer<typeof RequestResponseSchema>;

export const CreateRequestResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    id: z.string().uuid(),
    type: z.string(),
    status: z.string(),
    property: z.null(),
    user: z.object({
      id: z.number(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string().email(),
      podAdminId: z.number().nullable(),
      entity: z.object({
        id: z.number(),
        name: z.string(),
        streetName: z.string(),
        city: z.string(),
        streetNumber: z.string(),
        postalCode: z.string(),
        industry: z.string(),
        type: z.string(),
      }),
    }),
    requestDetails: z.array(z.any()),
    stakeholders: z.array(z.any()),
    invoice: z.null(),
    createdAt: z.string(),
    updatedAt: z.string(),
    evalionId: z.string(),
  }),
});

export const DetailedRequestSchema = z.object({
  id: z.string(),
  type: z.string(),
  status: z.string(),
  documentStatus: RequestDocumentStatusSchema,
  property: z
    .object({
      id: z.string(),
      streetName: z.string(),
      streetNumber: z.string(),
      country: z.string(),
      postalCode: z.string(),
      city: z.string(),
    })
    .nullable(),
  user: z.object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string().email(),
    podAdminId: z.number().nullable(),
    entity: z.object({
      id: z.number(),
      name: z.string(),
      streetName: z.string(),
      streetNumber: z.string(),
      country: z.string(),
      city: z.string(),
      postalCode: z.string(),
      industry: z.string(),
      type: z.string(),
      active: z.boolean(),
      createdAt: z.string(),
      updatedAt: z.string(),
      priority: EntityPrioritySchema.default("NOT_SET"),
    }),
  }),
  requestDetails: z.array(
    z.object({
      id: z.number(),
      fieldGroup: z.string(),
      fieldType: z.string(),
      fieldName: z.string(),
      fieldValue: z.string(),
    }),
  ),
  stakeholders: z.array(
    z.object({
      id: z.number(),
      title: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      email: z.string().email(),
      phoneNumber: z.string(),
      street: z.string(),
      houseNumber: z.string(),
      postalCode: z.string(),
      city: z.string(),
    }),
  ),
  invoice: z.any().nullable(),
  files: FileResponseSchema.array(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deadlineDate: z.string().nullable().optional(),
  evalionId: z.string(),
  lastNinoxSync: z.string().datetime().nullable(),
  viewingTypes: z.array(ViewingAddonIdentifiersEnum),
  viewing: RequestViewingStatusDetailSchema.nullable().optional(),
});

export type DetailedRequest = z.infer<typeof DetailedRequestSchema>;

export const DetailedRequestResponseSchema = z.object({
  status: z.string(),
  message: z.string(),
  data: DetailedRequestSchema,
});

export const RequestAllocationResponseSchema = z.object({
  status: z.string(),
  message: z.string(),
  data: z.object({
    id: z.string().uuid(),
    type: z.string(),
    status: z.string(),
    property: z
      .object({
        id: z.string().uuid(),
        streetName: z.string(),
        streetNumber: z.string(),
        country: z.string(),
        postalCode: z.string(),
        city: z.string(),
      })
      .nullable(),
    user: z.object({
      id: z.number().int(),
      firstName: z.string(),
      lastName: z.string(),
      podAdminId: z.number().nullable(),
    }),
    invoice: z.any().nullable(),
    files: z.array(z.any()),
    createdAt: z.string(),
    updatedAt: z.string(),
    evalionId: z.string(),
  }),
});

// New Schemas
export const OverviewRoleSchema = z.object({
  id: z.number(),
  name: z.string(),
});

export const OverviewUserSchema = z.object({
  id: z.number(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  active: z.boolean(),
  role: OverviewRoleSchema,
  evalionId: z.string(),
  entityEvalionId: z.string().optional(),
  phoneNumber: z.string(),
  entity: z.object({
    id: z.number(),
    name: z.string(),
    streetName: z.string(),
    streetNumber: z.string(),
    country: z.string(),
    city: z.string(),
    postalCode: z.string(),
    industry: z.string(),
    type: z.string(),
    active: z.boolean(),
    createdAt: z.string(),
    updatedAt: z.string(),
    stripePriceListId: z.string().nullable(),
    priority: EntityPrioritySchema.default("NOT_SET"),
  }),
});

export const OverviewStakeholderDetailSchema = z.object({
  id: z.number(),
  title: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  companyName: z.string().nullable(),
  type: z.string(),
  ownershipType: z.string().nullable(),
  email: z.string().email(),
  phoneNumber: z.string(),
  streetName: z.string(),
  streetNumber: z.string(),
  postalCode: z.string(),
  city: z.string(),
  country: z.string(),
  entryType: z.string(),
});
export type OverviewStakeholderDetail = z.infer<
  typeof OverviewStakeholderDetailSchema
>;
export const OverviewInvoiceDetailSchema = z.object({
  id: z.string(),
  status: z.string(),
  invoiceNumber: z.string(),
  invoiceUrl: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const OverviewFileDetailSchema = z.object({
  id: z.number(),
  originalName: z.string(),
  fileName: z.string(),
  filePath: z.string(),
  mimeType: z.string(),
  size: z.number(),
  systemFileTag: FileSystemTags,
  formFieldName: z.string().nullable(),
  category: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  signedUrl: z.string(),
  urlExpiresAt: z.string(),
  previewFilePath: z.string().nullable().optional(),
  previewSignedUrl: z.string().nullable().optional(),
  previewUrlExpiresAt: z.string().nullable().optional(),
  isExternalUpload: z.boolean(),
  duplicatedFromRequestId: z.string().nullable().optional(),
});

export const CreateRequestAllocationsSchema = <T>() =>
  z.object({
    property: CreatePropertySchema.extend({
      id: z.string().optional(),
    }).optional(),
    clientDetails: CreateStakeholderSchema.extend({
      id: z.number().optional(),
    }).optional(),
    invoiceRecipientDetails: CreateStakeholderSchema.extend({
      id: z.number().optional(),
    }).optional(),
    ownerDetails: z
      .array(CreateStakeholderSchema.extend({ id: z.number().optional() }))
      .optional(),
    requestDetails: z
      .array(CreateRequestDetailSchema.extend({ id: z.number().optional() }))
      .optional(),
    files: z.array(z.custom<T>()).optional(),
    // For additional metadata about files so that it can be processed accordingly in the backend
    filesSystemMeta: z
      .object({
        fieldName: z.string(),
      })
      .optional(),
    filesMeta: z
      .array(
        z.object({
          documentCategory: z.string().nullable().optional(),
          category: z.string().nullable().optional(),
          fieldName: z.string().nullable().optional(),
        }),
      )
      .optional(),
    status: RequestStatus.optional(),
    appliedDiscountCode: AppliedDiscountCodeSchema.nullable().optional(),
    requestMeta: z
      .object({
        isLiteRNDGSubmission: z.boolean().optional(),
        isContinuedLiteRNDGSubmission: z.boolean().optional(),
        summaryEdit: z
          .object({
            source: z.literal("externalPpaSummary"),
            step: z.union([
              z.literal(2),
              z.literal(3),
              z.literal(4),
              z.literal(6),
            ]),
          })
          .optional(),
      })
      .optional(),
    // For Embed Questionnaires
    personalData: CreateStakeholderSchema.extend({
      id: z.number().optional(),
    }).optional(),
  });

export type CreateRequestAllocations<T> = {
  property?: Omit<z.infer<typeof CreatePropertySchema>, "id"> & { id?: string };
  clientDetails?: Omit<z.infer<typeof CreateStakeholderSchema>, "id"> & {
    id?: number;
  };
  invoiceRecipientDetails?: Omit<
    z.infer<typeof CreateStakeholderSchema>,
    "id"
  > & { id?: number };
  // To indicate if the existing invoice recipient should be removed
  removeInvoiceRecipient?: boolean;
  ownerDetails?: Array<
    Omit<z.infer<typeof CreateStakeholderSchema>, "id"> & { id?: number }
  >;
  requestDetails?: Array<
    Omit<z.infer<typeof CreateRequestDetailSchema>, "id"> & { id?: number }
  >;
  files?: T[];
  appliedDiscountCode?: z.infer<typeof AppliedDiscountCodeSchema> | null;
  // For additional metadata about files so that it can be processed accordingly in the backend
  filesSystemMeta?: {
    fieldName: string;
  };
  filesMeta?: Array<{
    documentCategory?: string | null;
    category?: string | null;
    fieldName?: string | null;
  }>;
  requestMeta?: {
    isLiteRNDGSubmission?: boolean; // To indicate if the submission is from Lite RNDG form
    isContinuedLiteRNDGSubmission?: boolean; // To indicate if the submission is from Continued Lite RNDG form
    summaryEdit?: {
      source: "externalPpaSummary";
      step: 2 | 3 | 4 | 6;
    };
  };
  status?: RequestStatusType;
  // For Embed Questionnaires
  personalData?: Omit<z.infer<typeof CreateStakeholderSchema>, "id"> & {
    id?: number;
  };
};

export const ResidualUsefulLifeEvaluationResultsSchema = z.object({
  technicalMethodResult: z.number(),
  pointGridMethodResult: z.number(),
  finalAverageRUL: z.number(),
  finalRangeRUL: z.string(),
  annualDepreciation: z.number(),
});

export type ResidualUsefulLifeEvaluationResults = z.infer<
  typeof ResidualUsefulLifeEvaluationResultsSchema
>;

export const CreateRequestAllocationsResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    id: UuidSchema,
    type: z.string(),
    status: z.string(),
    documentStatus: RequestDocumentStatusSchema,
    user: OverviewUserSchema,
    property: OverviewPropertySchema.nullable(),
    requestDetails: z.array(OverviewRequestDetailSchema),
    clientDetails: OverviewStakeholderDetailSchema.nullable(),
    invoiceRecipientDetails: OverviewStakeholderDetailSchema.nullable(),
    ownerDetails: z.array(OverviewStakeholderDetailSchema).nullable(),
    invoice: InvoiceSchema.nullable(),
    files: z.array(
      OverviewFileDetailSchema.omit({ signedUrl: true, urlExpiresAt: true }),
    ),
    createdAt: z.string(),
    updatedAt: z.string(),
    evalionId: z.string(),
    // For Embed Questionnaires
    personalData: OverviewStakeholderDetailSchema.nullable(),
    propertyLinkedToMultipleRequests: z.boolean(),
    price: RequestPriceListSchema.nullable(),
    optionalAddonServices: StripePriceListSchema.nullable(),
    appraiser: z
      .object({
        id: z.string(),
        identifier: z.string(),
        name: z.string(),
      })
      .nullable(),
    sourceInfo: z
      .object({
        id: z.string(),
        source: SiteSourceEnum,
        termsOfServiceUrl: z.string().nullable(),
        privacyPolicyUrl: z.string().nullable(),
        redirectUrl: z.string().url().nullable().optional(),
      })
      .nullable(),
    requestIsLiteRNDG: z.boolean().nullable(),
    requestWasLiteRNDG: z.boolean().nullable(),
    liteSubmittedAt: z.string().nullable(),
    rulEvaluationResults: ResidualUsefulLifeEvaluationResultsSchema.nullable(),
    linkToFullForm: z.string().nullable(),
    submittedAt: z.string().nullable(),
    isExternal: z.boolean().nullable(),
    requestVariant: RequestVariantEnum.nullable(),
    isQualifiedLiteRequest: z.boolean().nullable(),
    isQualifiedLiteRequestUpdatedAt: z.string().nullable(),
    paymentLinkDispatch: z
      .object({
        deferredForViewingUpsell: z.boolean(),
        scheduledFor: z.date().or(z.string()).nullable().optional(),
        dispatchId: z.string().nullable().optional(),
      })
      .optional(),
    viewingUpsell: z
      .object({
        shouldShow: z.boolean(),
      })
      .optional(),
    documentUploadHandoffToken: z
      .object({
        token: z.string(),
        expiresAt: z.string(),
      })
      .nullable()
      .optional(),
  }),
});
export type CreateRequestAllocationsResponse = z.infer<
  typeof CreateRequestAllocationsResponseSchema
>;

export const UpdateRequestAllocationsSchema = <T>() =>
  z.object({
    property: CreatePropertySchema.extend({
      id: z.string().optional(),
    }).optional(),
    clientDetails: CreateStakeholderSchema.extend({
      id: z.number().optional(),
    }).optional(),
    invoiceRecipientDetails: CreateStakeholderSchema.extend({
      id: z.number().optional(),
    }).optional(),
    ownerDetails: z
      .array(CreateStakeholderSchema.extend({ id: z.number().optional() }))
      .optional(),
    requestDetails: z
      .array(CreateRequestDetailSchema.extend({ id: z.number().optional() }))
      .optional(),
    files: z.array(z.custom<T>()).optional(),
  });
export type UpdateRequestAllocations<T> = {
  property?: Omit<z.infer<typeof CreatePropertySchema>, "id"> & { id?: string };
  clientDetails?: z.infer<typeof CreateStakeholderSchema>;
  invoiceRecipientDetails?: z.infer<typeof CreateStakeholderSchema>;
  ownerDetails?: z.infer<typeof CreateStakeholderSchema>[];
  requestDetails?: z.infer<typeof CreateRequestDetailSchema>[];
  files?: T[];
};

export const DeleteRequestResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.null(),
});
export type DeleteRequestResponse = z.infer<typeof DeleteRequestResponseSchema>;

export const RequestDetailOverviewSchema = z.object({
  id: z.string().uuid(),
  type: z.string(),
  status: z.string(),
  documentStatus: RequestDocumentStatusSchema,
  stakeholders: z.array(z.any()),
  property: OverviewPropertySchema.nullable(),
  user: OverviewUserSchema,
  isFlagged: z.boolean(),
  entityPriority: EntityPrioritySchema.default("NOT_SET"),
  noAccountOwner: z.boolean(),
  accountOwner: UserSchema.nullable(),
  assignee: UserSchema.nullable(),
  assignedAt: z.string().nullable(),
  assignedBy: z.number().nullable(),
  assignedByUser: UserSchema.nullable(),
  invoice: InvoiceSchema.nullable(),
  files: z.array(OverviewFileDetailSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  deadlineDate: z.string().nullable().optional(),
  evalionId: z.string(),
  requiresNinoxSync: z.boolean(),
  lastNinoxSync: z.string().datetime().nullable(),
  ninoxSyncFailureAt: z.string().datetime().nullable().optional(),
  ninoxSyncFailureReason: z.string().nullable().optional(),
  requestDetails: z.array(OverviewRequestDetailSchema),
  customer: OverviewStakeholderDetailSchema.nullable(),
  initialAssessmentSent: z.object({
    isSent: z.boolean(),
    sentAt: z.string().datetime().nullable(),
    sentByName: z.string().nullable(),
    sendAction: z.enum(["send", "resend"]).nullable(),
  }),
  price: RequestPriceListSchema.nullable(),
  appraiser: z
    .object({
      id: z.string(),
      identifier: z.string(),
      name: z.string(),
      emailInfo: z
        .array(z.object({ supportEmail: z.string().email() }))
        .nullable(),
    })
    .nullable(),
  optionalAddonServices: StripePriceListSchema.nullable(),
  requestVariant: RequestVariantEnum,
  rulEvaluationResults: ResidualUsefulLifeEvaluationResultsSchema.nullable(),
  requestIsLiteRNDG: z.boolean().nullable(),
  requestWasLiteRNDG: z.boolean().nullable(),
  liteSubmittedAt: z.string().nullable(),
  linkToFullForm: z.string().nullable(),
  submittedAt: z.string().nullable(),
  isExternal: z.boolean(),
  isQualifiedLiteRequest: z.boolean().nullable(),
  isQualifiedLiteRequestUpdatedAt: z.string().nullable(),
  viewingTypes: z.array(ViewingAddonIdentifiersEnum),
  viewing: RequestViewingStatusDetailSchema.nullable().optional(),
  paymentLinkDispatch: z
    .object({
      deferredForViewingUpsell: z.boolean(),
      scheduledFor: z.date().or(z.string()).nullable().optional(),
      dispatchId: z.string().nullable().optional(),
    })
    .optional(),
  viewingUpsell: z
    .object({
      shouldShow: z.boolean(),
    })
    .optional(),
});

export type RequestDetailOverview = z.infer<typeof RequestDetailOverviewSchema>;

export const RequestDetailOverviewResponseSchema = z.object({
  status: StatusSchema, // Reusing your existing StatusSchema
  message: ResponseMessageSchema, // Reusing your existing ResponseMessageSchema
  data: RequestDetailOverviewSchema,
});

export type RequestDetailOverviewResponse = z.infer<
  typeof RequestDetailOverviewResponseSchema
>;

export const LiteResultsDataSchema = z.object({
  id: z.string().uuid(),
  rulEvaluationResults: ResidualUsefulLifeEvaluationResultsSchema.nullable(),
  price: RequestPriceListSchema.nullable(),
  submittedAt: z.string().nullable(),
  linkToFullForm: z.string().nullable(),
  property: OverviewPropertySchema.nullable(),
});

export type LiteResultsData = z.infer<typeof LiteResultsDataSchema>;

export const LiteResultsResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: LiteResultsDataSchema,
});

export type LiteResultsResponse = z.infer<typeof LiteResultsResponseSchema>;

export const GetExcellSheetReposeSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    s3FilePath: z.string(),
  }),
});
export type GetExcellSheetResponse = z.infer<typeof GetExcellSheetReposeSchema>;

export const RequestPatchOrchestratorSchema = z.object({
  requestIds: z.array(z.string()),
  assigneeId: z.union([z.number(), z.literal("unassigned")]).optional(),
  appraiserId: z.coerce.string().optional(),
  accountOwnerId: z
    .union([z.number(), z.literal("unassigned"), z.literal("no-owner")])
    .optional(),
  status: RequestStatus.optional(),
  documentStatus: RequestDocumentStatusSchema.optional(),
  newOwnerEmail: z.string().email().optional(),
  requesterEmail: z.string().email().optional(),
  // These are the only viewing options available for now
  // adding a new one requires frontend changes as well
  viewing: z
    .union([ViewingAddonIdentifiersEnum, RequestViewingPayloadSchema])
    .optional(),
  taxPlausibilityCheckService: LegalFeeAddonIdentifiersEnum.optional(),
  deadlineDate: z.string().datetime({ offset: true }).nullable().optional(),
  isFlagged: z.boolean().optional(),
  termsReconfirmed: z
    .object({
      acceptPrivacy: z.literal(true),
      acceptTerms: z.literal(true),
      acceptServiceWithdrawalTerms: z.literal(true),
    })
    .optional(),
});
export type RequestPatchOrchestrator = z.infer<
  typeof RequestPatchOrchestratorSchema
>;

export const PreviousRequestEntriesQuerySchema = z.object({
  entryType: z.enum(["property", "customer", "invoiceRecipient"]),
  requestType: z.string().optional(),
});
export type PreviousRequestEntriesQuery = z.infer<
  typeof PreviousRequestEntriesQuerySchema
>;

export const PreviousRequestEntriesDataSchema = z.union([
  z.array(OverviewPropertySchema),
  z.array(OverviewStakeholderDetailSchema),
]);

export type PreviousRequestEntriesData = z.infer<
  typeof PreviousRequestEntriesDataSchema
>;

export const PreviousRequestEntriesResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: PreviousRequestEntriesDataSchema,
});

export type PreviousRequestEntriesResponse = z.infer<
  typeof PreviousRequestEntriesResponseSchema
>;

export const SendRequestEmailSchema = z.object({
  emailType: z.enum([
    "initialAssessment",
    "finalAppraisal",
    "orderAfterInitialAssessment",
    "cancelRequestAfterInitialAssessment",
    "newRequestAdminEmail",
    "rndgLiteInitialAssessmentResults",
    "rndgLitePostPayOrderReceived",
    "rndgLiteOrderConfirmationAdminEmail",
    "externalFileUploadLinkEmail",
    "newExternalFilesUploadedAdminEmail",
    "manualInvoiceAndAppraisalEmail",
  ]),
  isExternalRequest: z.boolean().default(false),
});

export type SendRequestEmail = z.infer<typeof SendRequestEmailSchema>;

export const SendRequestEmailResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.null(),
});

export type SendRequestEmailResponse = z.infer<
  typeof SendRequestEmailResponseSchema
>;

export const GuestOrderAppraisalSchema = z.object({
  requestId: z.string().uuid(),
  isExternalRequest: z.boolean(),
  requestDetails: z.array(
    CreateRequestDetailSchema.extend({ id: z.number().optional() }),
  ),
  appliedDiscountCode: AppliedDiscountCodeSchema.nullable().optional(),
});

export type GuestOrderAppraisal = z.infer<typeof GuestOrderAppraisalSchema>;

export const GuestOrderAppraisalResponseSchema =
  RequestDetailOverviewResponseSchema;

export type GuestOrderAppraisalResponse = z.infer<
  typeof GuestOrderAppraisalResponseSchema
>;

export const RequestFilterOptionsSchema = z.object({
  status: z.array(
    z.object({
      value: z.string(),
      count: z.number(),
    }),
  ),
  typeOfRequest: z.array(
    z.object({
      value: z.string(),
      count: z.number(),
    }),
  ),
  paymentStatus: z.array(
    z.object({
      value: z.string(),
      count: z.number(),
    }),
  ),
  entityPriority: z.array(
    z.object({
      value: EntityPrioritySchema,
      count: z.number(),
    }),
  ),
  isFlagged: z.array(
    z.object({
      value: z.literal("true"),
      count: z.number(),
    }),
  ),
  requestVariant: z.array(
    z.object({
      value: z.string(),
      count: z.number(),
    }),
  ),
  viewing: z.array(
    z.object({
      value: z.string(),
      count: z.number(),
    }),
  ),
  accountOwner: z.array(
    z.object({
      label: z.string(),
      value: z.string(),
      count: z.number(),
    }),
  ),
  viewingStatus: z.array(
    z.object({
      value: z.string(),
      count: z.number(),
    }),
  ),
  documentStatus: z.array(
    z.object({
      value: RequestDocumentStatusSchema,
      count: z.number(),
    }),
  ),
});

export type RequestFilterOptions = z.infer<typeof RequestFilterOptionsSchema>;

export const RequestFiltersResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    internal: RequestFilterOptionsSchema,
    external: RequestFilterOptionsSchema,
  }),
});

export type RequestFiltersResponse = z.infer<
  typeof RequestFiltersResponseSchema
>;

export const CanEditRequestResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    canEdit: z.boolean(),
  }),
});

export type CanEditRequestResponse = z.infer<
  typeof CanEditRequestResponseSchema
>;

export const GuestRequestPatchOrchestratorSchema = z.discriminatedUnion(
  "action",
  [
    z.object({
      action: z.literal("cancelRequest"),
      isExternalRequest: z.boolean(),
    }),
    z.object({
      action: z.literal("stopReminders"),
      isExternalRequest: z.boolean(),
    }),
    z.object({
      action: z.literal("updateViewing"),
      data: z.object({
        viewing: ViewingAddonIdentifiersEnum,
        termsReconfirmed: z
          .object({
            acceptPrivacy: z.literal(true),
            acceptTerms: z.literal(true),
            acceptServiceWithdrawalTerms: z.literal(true),
          })
          .optional(),
      }),
      isExternalRequest: z.boolean(),
    }),
    z.object({
      action: z.literal("saveDocumentUploadLater"),
      documentUploadHandoffToken: z.string().min(1),
      data: z.object({
        expectedUploadDate: z.string().min(1),
        description: z.string().trim().optional(),
      }),
      isExternalRequest: z.boolean(),
    }),
    z.object({
      action: z.literal("saveDocumentUploadDecision"),
      documentUploadHandoffToken: z.string().min(1),
      data: z.object({
        documentStatus: GuestDocumentUploadDecisionStatusSchema,
      }),
      isExternalRequest: z.boolean(),
    }),
    z.object({
      action: z.literal("savePostIaCheckoutDetails"),
      data: z.object({
        requestDetails: z.array(
          CreateRequestDetailSchema.extend({ id: z.number().optional() }),
        ),
      }),
      isExternalRequest: z.boolean(),
    }),
  ],
);
export type GuestRequestPatchOrchestrator = z.infer<
  typeof GuestRequestPatchOrchestratorSchema
>;

export const GetRequestFiltersQuerySchema = z.object({
  appraiserId: z.string().optional(),
});
export type GetRequestFiltersQuery = z.infer<
  typeof GetRequestFiltersQuerySchema
>;

export const SyncRequestToNinoxSchema = z.object({
  id: z.string(),
  isTemp: z.boolean().optional().default(false),
});
export type SyncRequestToNinox = z.infer<typeof SyncRequestToNinoxSchema>;

const NinoxDiffEntrySchema = z.object({
  model: z.string(),
  fieldGroup: z.string(),
  fieldName: z.string(),
  fieldType: z.string(),
  id: z.string().nullable(),
  platforms: z.object({
    evalion: z.object({
      value: z.unknown(),
    }),
    ninox: z.object({
      value: z.unknown(),
    }),
  }),
});

export type NinoxDiffEntry = z.infer<typeof NinoxDiffEntrySchema>;

const NinoxDiffSchema = z.array(NinoxDiffEntrySchema);

export const NinoxDataSyncStatusSchema = z.object({
  recommendation: z.enum(["syncToNinox", "syncFromNinox", "review", "none"]),
  hasDifferences: z.boolean(),
  needsSyncToNinox: z.boolean(),
  needsSyncFromNinox: z.boolean(),
  diff: NinoxDiffSchema.optional(),
});

export const NinoxFilesSyncStatusSchema = z.object({
  needsFilesSyncToNinox: z.boolean(),
  missingFiles: z
    .array(
      z.object({
        id: z.number(),
        originalName: z.string(),
        filePath: z.string(),
      }),
    )
    .optional(),
  evalionFileCount: z.number().optional(),
  ninoxFileCount: z.number().optional(),
});

export const NinoxSyncStatusResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    data: NinoxDataSyncStatusSchema,
    files: NinoxFilesSyncStatusSchema,
    bannerType: z.enum([
      "none",
      "dataReview",
      "dataAndFiles",
      "ninoxData",
      "evalionData",
      "filesOnly",
    ]),
  }),
});

export type NinoxDataSyncStatus = z.infer<typeof NinoxDataSyncStatusSchema>;
export type NinoxFilesSyncStatus = z.infer<typeof NinoxFilesSyncStatusSchema>;
export type NinoxSyncStatusResponse = z.infer<
  typeof NinoxSyncStatusResponseSchema
>;

export const SyncFromNinoxResponseSchema = z.object({
  success: z.boolean(),
  message: z.string(),
});
export type SyncFromNinoxResponse = z.infer<typeof SyncFromNinoxResponseSchema>;

export const PartialSyncRequestToNinoxSchema = z.object({
  id: z.string(),
  isTemp: z.boolean().optional().default(false),
  fields: z
    .array(z.enum(["viewing"]))
    .min(1)
    .default(["viewing"]),
});
export type PartialSyncRequestToNinox = z.infer<
  typeof PartialSyncRequestToNinoxSchema
>;

export const ResolveNinoxDiffSchema = z.object({
  id: z.string(),
  isTemp: z.boolean().optional().default(false),
  tableId: z.enum(["B", "C", "F"]).optional().default("F"),
  resolvedPayload: z.record(z.unknown()),
});
export type ResolveNinoxDiff = z.infer<typeof ResolveNinoxDiffSchema>;

export const ResolutionsModelEnum = z.enum([
  "invoice",
  "property",
  "request",
  "requestDetail",
  "stakeholder",
  "additionalServices",
]);
export type ResolutionsModelEnumType = z.infer<typeof ResolutionsModelEnum>;

export const ResolveDataConflictSchema = z.object({
  requestId: z.string(),
  isTemp: z.boolean(),
  resolutions: z.array(
    z.object({
      model: ResolutionsModelEnum,
      id: z.union([z.string(), z.number(), z.null()]),
      fields: z.record(z.string(), z.unknown()),
    }),
  ),
});
export const ResolveDataConflictsSchema = z.array(ResolveDataConflictSchema);
export type ResolveDataConflict = z.infer<typeof ResolveDataConflictSchema>;
export type ResolveDataConflicts = z.infer<typeof ResolveDataConflictsSchema>;

export const GetNinoxSyncStatusQuerySchema = z.object({
  id: z.string(),
  isTemp: z
    .union([
      z.boolean(),
      z.enum(["true", "false"]).transform((val) => val === "true"),
    ])
    .optional()
    .default(false),
});

export type GetNinoxSyncStatusQuery = z.infer<
  typeof GetNinoxSyncStatusQuerySchema
>;

export const RegenerateGuestRequestUploadLinkSchema = z.object({
  requestId: z.string().uuid(),
  isExternal: coerceBoolean(),
});

export type RegenerateGuestRequestUploadLink = z.infer<
  typeof RegenerateGuestRequestUploadLinkSchema
>;

export type GuestRequestUploadLinkQuery = z.infer<
  typeof RegenerateGuestRequestUploadLinkSchema
>;
export const GuestRequestUploadTokenParamsSchema = z.object({
  token: z.string().min(32),
});

export type GuestRequestUploadTokenParams = z.infer<
  typeof GuestRequestUploadTokenParamsSchema
>;

export const CreateContinuedLiteDocumentUploadTokenSchema = z.object({
  isExternalRequest: z.boolean(),
  documentUploadHandoffToken: z.string().min(1),
});

export type CreateContinuedLiteDocumentUploadToken = z.infer<
  typeof CreateContinuedLiteDocumentUploadTokenSchema
>;

export const CreateContinuedLiteDocumentUploadTokenResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    token: z.string(),
    expiresAt: z.string(),
  }),
});

export type CreateContinuedLiteDocumentUploadTokenResponse = z.infer<
  typeof CreateContinuedLiteDocumentUploadTokenResponseSchema
>;

export const RegenerateExternalFileUploadLinkResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    token: z.string(),
    uploadUrl: z.string(),
    requestId: z.string().uuid(),
    isExternal: z.boolean(),
    expiresAt: z.string(),
    lastSentAt: z.string().nullable().optional(),
  }),
});

export type RegenerateExternalFileUploadLinkResponse = z.infer<
  typeof RegenerateExternalFileUploadLinkResponseSchema
>;

export const ValidateExternalFileUploadTokenResponseSchema = z.object({
  status: StatusSchema,
  message: ResponseMessageSchema,
  data: z.object({
    valid: z.boolean(),
    requestId: z.string().uuid(),
    isExternal: z.boolean(),
  }),
});

export type ValidateExternalFileUploadTokenResponse = z.infer<
  typeof ValidateExternalFileUploadTokenResponseSchema
>;
